// hooks/useAcademicPlan.ts
/**
 * Academic Plan Hook - Main Entry Point
 *
 * V2 UPDATE: Integrated decision logging for USER_STATE_REDESIGN
 * V3 REFACTOR: Composed from split hooks for maintainability
 *
 * This hook composes the split academic plan hooks into a single
 * unified interface for backward compatibility. Individual hooks
 * can also be imported directly from their files in ./academicPlan/ when needed.
 */
import { useState, useMemo } from 'react';
import type { AcademicPlan, EnrolledCourse, SemesterCode } from '../types';

// Import composed hooks and their types
import { usePlanCore, type SuggestionsPayload } from './academicPlan/usePlanCore';
import { useCourseOperations, type CourseOperations, type CourseInput } from './academicPlan/useCourseOperations';
import { useBookmarkOperations, type BookmarkOperations, type BookmarkCourseInput } from './academicPlan/useBookmarkOperations';
import { usePlaceholderOperations, type PlaceholderOperations, type PlaceholderUpdates, type ConvertCourseInput } from './academicPlan/usePlaceholderOperations';
import { useBulkOperations, type BulkOperations } from './academicPlan/useBulkOperations';
import type { CreatePlaceholderOptions, PlaceholderCourse } from './academicPlan/helpers';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course requirements mapping - tracks which requirements each course fulfills
 */
export interface CourseRequirementsMap {
  [courseCode: string]: string[];
}

/**
 * Combined plan operations interface
 */
export interface PlanOperations {
  // Course operations
  addCourse: (course: CourseInput, semester: SemesterCode) => Promise<boolean>;
  addCourses: CourseOperations['addCourses'];
  reorderCourse: (semesterKey: SemesterCode, fromIndex: number, toIndex: number) => Promise<void>;
  addCourseAtPosition: (course: CourseInput, semesterKey: SemesterCode, position: number) => Promise<boolean>;
  removeCourse: (semester: SemesterCode, courseIndex: number, skipConfirmation?: boolean) => Promise<void>;
  moveCourse: (course: CourseInput, fromSemester: SemesterCode, toSemester: SemesterCode, fromIndex: number) => Promise<void>;

  // Bookmark operations
  addToBookmarks: (course: BookmarkCourseInput) => Promise<void>;
  removeFromBookmarks: (courseIndex: number) => Promise<void>;
  removeBookmarkByCode: (courseCode: string) => Promise<void>;
  toggleBookmark: (course: BookmarkCourseInput) => void;

  // Placeholder operations
  createPlaceholder: (placeholderData: CreatePlaceholderOptions, targetSemester?: SemesterCode | null) => PlaceholderCourse;
  updatePlaceholder: (placeholderCode: string, updates: PlaceholderUpdates) => void;
  convertPlaceholder: (placeholderCode: string, realCourse: ConvertCourseInput, fromSemester: SemesterCode, fromIndex: number) => Promise<void>;

  // Bulk operations
  clearSemester: (semester: SemesterCode) => Promise<void>;
  clearAllCourses: (options?: { includePlaceholders?: boolean; skipConfirm?: boolean }) => Promise<void>;
  applyTemplate: (distributionPlan: Record<string, EnrolledCourse[]>) => Promise<void>;

  // Course requirements
  updateCourseRequirements: (courseCode: string, newRequirements: string[]) => void;

  // Plan refresh
  refreshPlan: () => Promise<void>;

  // Optimistic suggestion clear (instant local state update)
  clearSuggestionsLocally: () => void;

  // Optimistic suggestion merge (instant local state update after generate)
  mergeSuggestionsLocally: (payload: SuggestionsPayload) => void;

  // Direct state setter for external updates
  updateAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
}

/**
 * Plan utility functions
 */
export interface PlanUtils {
  canFulfillPlaceholder: (course: EnrolledCourse, placeholder: EnrolledCourse) => boolean;
}

/**
 * Return type for the useAcademicPlan hook
 */
export interface UseAcademicPlanReturn {
  academicPlan: AcademicPlan;
  courseRequirements: CourseRequirementsMap;
  planOps: PlanOperations;
  planUtils: PlanUtils;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  saving: boolean;
  currentPlanId: number | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to manage academic plan state and operations with backend API
 * Authentication is required - no anonymous plan support
 * @param activePlanId - The ID of the currently active plan
 * @returns Academic plan state and operations
 */
export const useAcademicPlan = (activePlanId: number | null = null): UseAcademicPlanReturn => {
  // Core state and operations
  const {
    academicPlan,
    setAcademicPlan,
    loading,
    refreshing,
    error,
    saving,
    currentPlanId,
    savePlanToBackend,
    fetchAcademicPlan,
    refreshPlan,
    clearSuggestionsLocally,
    mergeSuggestionsLocally,
  } = usePlanCore(activePlanId);

  // Course requirements state (tracks which requirements each course fulfills)
  const [courseRequirements, setCourseRequirements] = useState<CourseRequirementsMap>(() => {
    // Initialize with default requirements based on course types
    const requirements: CourseRequirementsMap = {};

    // Core CS courses
    ['CMSC131', 'CMSC132', 'CMSC216', 'CMSC250', 'CMSC330', 'CMSC351'].forEach(code => {
      requirements[code] = ['core-cs'];
    });

    // Common math courses
    requirements['MATH140'] = ['math'];
    requirements['MATH141'] = ['math'];
    requirements['STAT400'] = ['math'];

    // CS Area courses (examples)
    ['CMSC411', 'CMSC412', 'CMSC414', 'CMSC416', 'CMSC417'].forEach(code => {
      requirements[code] = requirements[code] || [];
      requirements[code].push('cs-area-systems', 'upper-level-cs');
    });

    return requirements;
  });

  // Course operations
  const courseOps: CourseOperations = useCourseOperations({
    academicPlan,
    setAcademicPlan,
    currentPlanId,
    savePlanToBackend,
    refreshPlan
  });

  // Bookmark operations
  const bookmarkOps: BookmarkOperations = useBookmarkOperations({
    academicPlan,
    setAcademicPlan,
    currentPlanId
  });

  // Placeholder operations (depends on course and bookmark ops)
  const placeholderOps: PlaceholderOperations = usePlaceholderOperations({
    setAcademicPlan,
    addCourse: courseOps.addCourse,
    addToBookmarks: bookmarkOps.addToBookmarks,
    removeCourse: courseOps.removeCourse,
    currentPlanId,
    savePlanToBackend,
    refreshPlan,
    academicPlan
  });

  // Bulk operations (depends on course ops)
  const bulkOps: BulkOperations = useBulkOperations({
    academicPlan,
    setAcademicPlan,
    currentPlanId,
    savePlanToBackend,
    fetchAcademicPlan,
    addCourse: courseOps.addCourse
  });

  // Memoize planOps to prevent recreation on every render
  // Combines all operations into the legacy interface
  const planOps: PlanOperations = useMemo(() => ({
    // Course operations
    addCourse: courseOps.addCourse,
    addCourses: courseOps.addCourses,
    reorderCourse: courseOps.reorderCourse,
    addCourseAtPosition: courseOps.addCourseAtPosition,
    removeCourse: courseOps.removeCourse,
    moveCourse: courseOps.moveCourse,

    // Bookmark operations
    addToBookmarks: bookmarkOps.addToBookmarks,
    removeFromBookmarks: bookmarkOps.removeFromBookmarks,
    removeBookmarkByCode: bookmarkOps.removeBookmarkByCode,
    toggleBookmark: bookmarkOps.toggleBookmark,

    // Placeholder operations
    createPlaceholder: placeholderOps.createPlaceholder,
    updatePlaceholder: placeholderOps.updatePlaceholder,
    convertPlaceholder: placeholderOps.convertPlaceholder,

    // Bulk operations
    clearSemester: bulkOps.clearSemester,
    clearAllCourses: bulkOps.clearAllCourses,
    applyTemplate: bulkOps.applyTemplate,

    // Course requirements
    updateCourseRequirements: (courseCode: string, newRequirements: string[]) => {
      setCourseRequirements(prev => ({
        ...prev,
        [courseCode]: newRequirements
      }));
    },

    // Plan refresh
    refreshPlan,

    // Optimistic suggestion clear
    clearSuggestionsLocally,

    // Optimistic suggestion merge
    mergeSuggestionsLocally,

    // Direct state setter for external updates
    updateAcademicPlan: setAcademicPlan
  }), [
    courseOps,
    bookmarkOps,
    placeholderOps,
    bulkOps,
    refreshPlan,
    clearSuggestionsLocally,
    mergeSuggestionsLocally,
    setAcademicPlan
  ]);

  // Memoize planUtils to prevent recreation
  const planUtils: PlanUtils = useMemo(() => ({
    canFulfillPlaceholder: (course: EnrolledCourse, placeholder: EnrolledCourse): boolean => {
      if (!placeholder.isPlaceholder) return false;

      // Cast to PlaceholderCourse to access requirements
      const placeholderCourse = placeholder as PlaceholderCourse;
      const reqs = placeholderCourse.requirements;

      if (!reqs) return true;

      // Check department
      if (reqs.department && !course.code.startsWith(reqs.department)) {
        return false;
      }

      // Check level
      const courseNumber = parseInt(course.code.match(/\d+/)?.[0] || '0');
      if (reqs.levelMin && courseNumber < reqs.levelMin) return false;
      if (reqs.levelMax && courseNumber > reqs.levelMax) return false;

      // Check GenEd requirements
      if (reqs.genEdCategories && reqs.genEdCategories.length > 0) {
        const courseGenEds = (course as { gen_ed_requirements?: string[] }).gen_ed_requirements || [];
        const hasRequiredGenEd = reqs.genEdCategories.some(req =>
          courseGenEds.includes(req)
        );
        if (!hasRequiredGenEd) return false;
      }

      return true;
    }
  }), []);

  // Memoize the entire return object to prevent recreation
  return useMemo(() => ({
    academicPlan, // Contains semesters in NUMERIC format for proper sorting
    courseRequirements,
    planOps,
    planUtils,
    loading,
    refreshing,
    error,
    saving,
    currentPlanId
  }), [
    academicPlan,
    courseRequirements,
    planOps,
    planUtils,
    loading,
    refreshing,
    error,
    saving,
    currentPlanId
  ]);
};
